// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * TermsPage - /voorwaarden
 * Algemene voorwaarden WilsApp pilotversie.
 */
import { BackButton } from '@/components/ui/BackButton'
import { Card }       from '@/components/ui/Card'

const SECTIONS = [
  {
    title: 'Over WilsApp',
    content: [
      'WilsApp is een hulpmiddel waarmee u uw wensen en persoonlijke informatie kunt vastleggen voor uw naasten. WilsApp is geen vervanger van juridisch, medisch of notarieel advies.',
      'WilsApp is geen bronhouder, geen BRP-systeem, geen notarieel systeem en geen DigiD-oplossing. WilsApp fungeert uitsluitend als organisatie- en informatieoverzicht voor persoonlijk gebruik.',
    ],
  },
  {
    title: 'Wat WilsApp niet is',
    content: [
      '• Geen juridisch bindend document: de informatie die u vastlegt in WilsApp heeft geen juridische geldigheid tenzij dit afzonderlijk is vastgelegd bij een notaris of bevoegde instantie.',
      '• Geen medisch advies: medische wensen in WilsApp, waaronder DNAR-instructies, zijn geen officiële medische wilsverklaringen. Bespreek dit altijd met uw huisarts.',
      '• Geen officieel testament: informatie over erfgenamen, executeurs en voogdij in WilsApp is niet juridisch bindend. Een rechtsgeldig testament wordt opgemaakt bij een notaris.',
      '• Geen levenstestament: informatie over volmachten en vertegenwoordiging in WilsApp vervangt geen notarieel levenstestament.',
    ],
  },
  {
    title: 'Gebruik en aansprakelijkheid',
    content: [
      'WilsApp wordt aangeboden als hulpmiddel tijdens de pilotfase. Er wordt geen garantie gegeven over de volledigheid, juistheid of beschikbaarheid van de informatie en functionaliteit.',
      'WilsApp is niet aansprakelijk voor schade die voortvloeit uit het gebruik van de applicatie, verlies van gegevens door apparaatdefecten, onjuiste of onvolledige informatie ingevuld door de gebruiker, of handelen van nabestaanden op basis van in WilsApp vastgelegde informatie.',
      'De gebruiker is zelf verantwoordelijk voor het correct en volledig invullen van de gewenste informatie, en voor het op de hoogte houden van de aangewezen vertrouwenspersoon.',
    ],
  },
  {
    title: 'Vertrouwenspersoon',
    content: [
      'Door een vertrouwenspersoon aan te wijzen en een sleutelcode te overhandigen, geeft u die persoon de mogelijkheid om bepaalde modules in WilsApp in te zien.',
      'De vertrouwenspersoon is geen wettelijk rechtsfiguur in de zin van het Burgerlijk Wetboek. Het aanwijzen van een vertrouwenspersoon in WilsApp creëert geen wettelijke bevoegdheid of aansprakelijkheid.',
      'WilsApp verifieert niet of de profieleigenaar daadwerkelijk is overleden op het moment van toegang. Het gebruik van de sleutelcode is een vertrouwenshandeling tussen u en de door u aangewezen persoon.',
    ],
  },
  {
    title: 'Gegevens en privacy',
    content: [
      'Zie de volledige privacyverklaring op /privacy voor informatie over de verwerking van uw persoonsgegevens.',
    ],
  },
  {
    title: 'Beschikbaarheid',
    content: [
      'WilsApp is een pilotproduct. Er kan geen garantie worden gegeven voor doorlopende beschikbaarheid of toekomstige ondersteuning. U wordt tijdig geïnformeerd bij beëindiging van de pilot.',
      'Het wordt ten sterkste aanbevolen om uw vastgelegde informatie ook op een andere manier (papier, brief aan nabestaanden) te bewaren.',
    ],
  },
  {
    title: 'Toepasselijk recht',
    content: [
      'Op het gebruik van WilsApp is Nederlands recht van toepassing.',
      'Versie: 1.0 - Pilotversie 2026',
    ],
  },
  {
    title: 'Pilotversie en vertrouwelijk gebruik',
    content: [
      'Deze versie van WilsApp is uitsluitend bedoeld als pilot- en testversie. De applicatie wordt beschikbaar gesteld voor evaluatie, testen en het verzamelen van gebruikerservaringen.',
      'Het is niet toegestaan deze pilotversie, of onderdelen daarvan, zonder voorafgaande schriftelijke toestemming van de rechthebbende te kopiëren, verspreiden, publiceren, door te sturen, beschikbaar te stellen aan derden of op andere wijze openbaar te maken.',
      'Feedback op deze pilot wordt gewaardeerd en kan worden gebruikt voor de verdere ontwikkeling van WilsApp.',
    ],
  },
  {
    title: 'Maker en rechthebbende',
    content: [
      'WilsApp is een zelfstandig ontwikkeld Nederlands softwareplatform, ontworpen en ontwikkeld door J.J.S. van der Werf.',
      'De software, broncode, vormgeving, teksten, databasestructuur, gebruikersinterface en het onderliggende concept worden beschermd door het auteursrecht en andere intellectuele eigendomsrechten.',
      'J.J.S. van der Werf is rechthebbende van WilsApp, tenzij voor specifieke onderdelen uitdrukkelijk anders is vermeld.',
      'Het concept en de ontwikkeling van WilsApp zijn vastgelegd in meerdere i-DEPOT-registraties bij het Benelux-Bureau voor de Intellectuele Eigendom (BOIP) als dateerbaar bewijs van auteurschap.',
      '© 2024–2026 J.J.S. van der Werf. Alle rechten voorbehouden.',
    ],
  },
]

export function TermsPage() {
  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <BackButton />

      <div>
        <h1 className="text-xl font-semibold text-ink mb-1">Algemene voorwaarden</h1>
        <p className="text-sm text-ink-3 leading-relaxed">
          Pilotversie WilsApp - lees deze voorwaarden voordat u de applicatie gebruikt.
        </p>
      </div>

      {SECTIONS.map((section, i) => (
        <Card key={i}>
          <h2 className="text-sm font-semibold text-ink mb-3">{section.title}</h2>
          <div className="flex flex-col gap-1.5">
            {section.content.map((line, j) => (
              <p key={j} className="text-xs text-ink-3 leading-relaxed">{line}</p>
            ))}
          </div>
        </Card>
      ))}

      <p className="text-center text-xs text-ink-4 pb-4">
        Vragen? Stuur een e-mail naar info@wilsapp.nl
      </p>
    </div>
  )
}
